import {
  type SignupSourceData,
  v2GetSignupQuestion,
  v2RecordSignupAnswer,
  v2RecordSignupSource,
} from '@workspace/api-backend'
import type { SignupQuestionSource } from '@workspace/seedforge-features/onboarding'
import { useEffect } from 'react'

/** URL keys the website and /s/ pages put on links into the app, and the field each one fills. */
const SOURCE_KEYS = {
  ref: 'ref',
  landing: 'landing',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_id: 'utmId',
  utm_term: 'utmTerm',
  share_link: 'shareLink',
} as const satisfies Record<string, keyof SignupSourceData>

/** The backend stores 200 characters per value; longer ones are cut here so one can't void the rest. */
const MAX_LENGTH = 200

/**
 * Where this visitor came from, read once when the app loads and kept in
 * memory only: no cookie, no browser storage. Only the URL counts (the website
 * already turns its own referrer into `ref`), so the values are the same after
 * a Google sign-in, which returns to the full current URL. See AD-030.
 */
export function arrivalSourceFrom(search: string, pathname: string): SignupSourceData {
  const params = new URLSearchParams(search)
  const source: SignupSourceData = { appEntry: pathname.slice(0, MAX_LENGTH) }
  for (const [param, field] of Object.entries(SOURCE_KEYS)) {
    const value = params.get(param)
    if (value) source[field] = value.slice(0, MAX_LENGTH)
  }
  return source
}

const arrivalSource = arrivalSourceFrom(window.location.search, window.location.pathname)

/** Only an account this young reports a source; the backend checks again (24 h). */
const NEW_ACCOUNT_MS = 60 * 60 * 1000
let reported = false

/**
 * Sends the arrival source once, as soon as a new account's session appears
 * (email code or Google). The backend keeps only the first value per account.
 * A failure is ignored: it can only cost us the source, never the sign-up.
 */
export function useReportSignupSource(userId: string | undefined, createdAt: Date | undefined) {
  useEffect(() => {
    if (!userId || !createdAt || reported) return
    if (Date.now() - new Date(createdAt).getTime() > NEW_ACCOUNT_MS) return
    reported = true
    v2RecordSignupSource({ body: arrivalSource }).catch(() => {})
  }, [userId, createdAt])
}

/** Loads and saves the sign-up question shown during onboarding. */
export const signupQuestionSource: SignupQuestionSource = {
  async load() {
    const res = await v2GetSignupQuestion({ throwOnError: true })
    return res.data.data.config
  },
  async record(answer) {
    await v2RecordSignupAnswer({ body: answer, throwOnError: true })
  },
}
